<script lang="ts" setup>
  import { useUnitFormatter } from '@othercommerce/domain';
  import { useHttpClient, useShared, useRoute } from '@othercommerce/foundation';
  import type { OrderItemWithStock } from '@storefront/resources';
  import { ref } from 'vue';
  import { removeFromCartEvent, useGoogleTagManager } from '@storefront/storefront';

  interface Props {
    item: OrderItemWithStock;
  }

  defineProps<Props>();

  const route = useRoute();
  const http = useHttpClient();
  const shared = useShared();
  const unitFormatter = useUnitFormatter();
  const gtm = useGoogleTagManager();

  const loading = ref(false);

  function onDestroy(item: OrderItemWithStock) {
    loading.value = true;
    http.delete(route('web.cart.item.destroy', { item: item.id })).then(() => {
      gtm.push(removeFromCartEvent(item, item.amount, shared.value.activeCurrencyCode));
      loading.value = false;
    }).catch(() => {
      loading.value = false;
    });
  }

  function formatItemUnit(item: OrderItemWithStock) {
    return unitFormatter.short(item.stockUnit, item.amount);
  }
</script>
<template>
  <SfListCard class="drawer-cart-item" interactive>
    <template #image>
      <MediaPicture :photo="item.photo" class="drawer-cart-item__product-image" sizes="150x150 2x, 75x75" />
    </template>
    <template #default>
      <header>
        <h3 v-if="item.variantTitle" class="drawer-cart-item__product-title">{{ item.variantTitle }}</h3>
        <p v-if="item.variantSku" class="drawer-cart-item__product-sku">{{ $t('SHOP.CART.LABEL.SKU') }} {{ item.variantSku }}</p>
      </header>
      <SfProductCatalogPrice :item="item" />
      <div class="drawer-cart-item__product-details">
        <SfProductPrice :price="item" class="drawer-cart-item__product-price" />
        <p class="drawer-cart-item__product-count" v-html="formatItemUnit(item)" />
      </div>
      <SfProductOmnibus :price="item" />
    </template>
    <template #icons>
      <SfButton :disabled="loading" :loading :title="$t('SHOP.CART.ARIA.REMOVE_FROM_CART')" danger square transparent @click.stop="onDestroy(item)" @keydown.enter.stop="onDestroy(item)">
        <SfIconTrash aria-hidden="true" />
      </SfButton>
    </template>
  </SfListCard>

</template>
<style lang="scss">
  @use "@storefront/styles/mixins/media";

  :root {
    --sf-cart-drawer-list-card-padding-y: 0.5rem;
    --sf-cart-drawer-list-card-padding-x: 0.5rem;
  }

  .drawer-cart-item {
    --sf-list-card-padding-y: var(--sf-cart-drawer-list-card-padding-y);
    --sf-list-card-padding-x: var(--sf-cart-drawer-list-card-padding-x);
    --sf-list-card-background-hover: var(--background-default);

    &.list-card {
      flex: none;
    }

    &__product-image img {
      width: 75px;
      aspect-ratio: 1;
    }

    &__product-title {
      font-size: 1rem;
    }

    &__product-sku {
      font-size: 0.75rem;
      color: var(--color-secondary);
    }

    &__product-details {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
    }

    &__product-price {
      font-size: 1rem;
      color: var(--color-black);
      font-weight: var(--font-weight-bold);
    }

    &__product-count {
      font-size: 0.75rem;
    }
  }
</style>
